<template>
	<aside
		id="right-aside"
		class="arm-aside"
		:class="{ 'arm-aside--slideout': setup.ui.asideRightSlideOut }"/>

	<aside
		id="left-aside"
		class="arm-aside"
		:class="{ 'arm-aside--slideout': setup.ui.asideLeftSlideOut }"/>

	<div
		v-if="!setup.isTouch"
		class="arm-aside-trigger-mouse">
		<shared-magnetic-blob
			:side="setup.ui.asideLeftSlideOut ? 'left' : 'right'"
			:proximity-factor="0.5"
			:curve-factor="1"
			@path-click="setup.toggleLeftAside()">
			<template #icon="{ x, y }">
				<o-icon
					class="arm-aside-trigger-mouse__icon"
					font-icon="icon-interface-arrows-right"
					:style="{
						top: `${y}%`,
						transform: `translate3d(${-(100 - x)}%, -75%, 0)`,
						opacity: x / 100,
					}"/>
			</template>
		</shared-magnetic-blob>
	</div>

	<div
		v-if="!setup.isTouch"
		class="arm-aside-trigger-overlay"
		@click="setup.toggleLeftAside(false)"/>

	<nav
		v-if="setup.self.isConnected"
		id="tour-navigation"
		:ref="setup._armNav"
		class="arm-nav">
		<img
			class="arm-nav__logo"
			src="@/assets/img/logo/armado.svg"
			alt="Armado">
		<img
			class="arm-nav__logo white"
			src="@/assets/img/logo/armado-white.svg"
			alt="Armado">
		<img
			class="arm-nav__logo--a"
			src="@/assets/img/logo/a-armado.svg"
			alt="Armado">

		<o-button
			v-if="setup.responsive.onPhone"
			id="tour-navigation-menu"
			class="arm-nav__mobile-aside-trigger"
			suffix-font-icon="icon-hamburger-menu-1"
			nude
			@click="setup._asideMobileNav.value?.open()">
			{{ $t('common.shared.layout.menu') }}
		</o-button>

		<shared-main-nav
			v-if="!setup.responsive.onPhone"
			:unread-news-count="setup.unreadNewsCount"/>

		<div class="arm-nav__footer">
			<div
				v-if="!setup.self.isTempWorker() && !setup.responsive.onPhone && setup.self.organizationUserId && setup.self.roleUsers.length"
				class="arm-nav__footer-roles flex g-sm fd-c">
				<v-dropdown
					class="arm-nav-menu-classic"
					:shown="setup.showPermissionTooltip"
					:triggers="['click']"
					:placement="!setup.ui.onLayoutNavCollapsed ? 'top' : 'right'"
					@apply-show="setup.handlePopoverShowAsync()"
					@apply-hide="setup.handlePopoverHide()">
					<div class="orion-nav-main__item">
						<o-icon
							class="orion-nav-main__item-main-icon"
							font-icon="icon-interface-edit-user-experience-design"/>
						<span class="arm-nav-menu__item arm-nav-menu__item--parent">{{ $t('common.shared.layout.rolesPermissions') }}</span>
					</div>

					<template #popper>
						<div class="permission-popover">
							<div v-if="setup.roles.length">
								<h4>{{ $t('common.shared.layout.yourRoles') }}</h4>
								<div v-if="setup.roles.length || setup.loading">
									<div
										v-for="role in setup.roles"
										:key="role.id"
										class="permission-popover__item">
										<div
											class="permission-popover__color"
											:class="`background--${role.colorName}`"/>
										<span>{{ role.name }}</span>
									</div>
								</div>
							</div>
							<div v-if="setup.tags.length">
								<h4>{{ $t('common.shared.layout.yourTags') }}</h4>
								<div v-if="setup.tags.length || setup.loading">
									<div
										v-for="tag in setup.tags"
										:key="tag.id"
										class="permission-popover__item">
										<div
											class="permission-popover__color"
											:class="`background--${tag.tagColorName}`"/>
										<span>{{ tag.tagName }}</span>
									</div>
								</div>
							</div>
							<o-button
								v-if="setup.self.organizationUserId"
								color="info"
								outline
								@click="setup.openUserRolesAside()">
								{{ $t('common.shared.layout.seeDetails') }}
							</o-button>
							<o-loader
								:visible="setup.loading"
								size="md"
								:message="$t('common.shared.layout.loading')"/>
						</div>
					</template>
				</v-dropdown>
			</div>

			<o-select
				v-model="setup.selectedLocale"
				:options="setup.localeOptions"
				track-key="value"
				value-key="value"
				:display-key="setup.ui.onLayoutNavFull ? 'label' : 'flag'">
				<template
					v-if="!setup.ui.onLayoutNavFull"
					#option="{ item }">
					<span>{{ item.label }}</span>
				</template>
			</o-select>

			<div
				class="flex ai-c jc-sb g-sm"
				:class="{ 'fd-c': setup.ui.onLayoutNavCollapsed }">
				<div
					id="navigation-tour-profile"
					class="arm-nav__user"
					@click="setup.showUserProfile()">
					<o-avatar
						style="cursor: pointer;"
						:size="40"
						:root-url="!setup.self.suiteUserId ? '/app/avatar/' : undefined"
						:name="setup.self.name"
						:avatar="setup.self.avatarUrl"
						:nb-avatar-updates="setup.ui.token"/>

					<div class="arm-nav__user-name">
						{{ setup.self.name }}
					</div>
				</div>

				<o-icon
					id="navigation-tour-logout"
					v-tooltip="$t('common.shared.layout.logout')"
					class="arm-nav__logout"
					font-icon="icon-interface-logout-circle"
					ripple="danger"
					@click="setup.self.logoutAsync()"/>
			</div>
		</div>
	</nav>

	<main
		v-if="setup.self.isConnected"
		id="arm-main"
		class="arm-main"
		:class="{
			'arm-main--scroll-down': setup.scrollDirection === 'down',
			'arm-main--scroll-up': setup.scrollDirection === 'up',
		}">
		<div
			v-if="setup.isTouch"
			class="orion-page arm-aside-trigger-touch">
			<div
				class="arm-aside-trigger-overlay"
				@click="setup.toggleLeftAside(false)"/>
			<o-button
				class="arm-aside-trigger-touch__button"
				prefix-font-icon="icon-interface-arrows-move-right"
				color="brand"
				@click="setup.toggleLeftAside()"/>
		</div>

		<router-view/>

		<footer class="arm-footer">
			<div>{{ $t('common.shared.footer.copyright') }}</div>
			<div class="legal-links">
				<a
					href="/Legal"
					target="_blank">{{ $t('common.shared.footer.legalNotice') }}</a>
				<a
					href="/Legal#cgu"
					target="_blank">{{ $t('common.shared.footer.termsAndPolicies') }}</a>
			</div>
			<div>{{ $t('shared.footer.version', { version: appVersion }) }}</div>
		</footer>
	</main>

	<tour-register/>

	<shared-discussions v-if="setup.self.isConnected"/>

	<shared-botpress v-if="setup.self.isConnected && setup.self.isTempWorker()"/>

	<o-aside
		v-if="setup.self.isConnected && setup.responsive.onPhone"
		:ref="setup._asideMobileNav"
		:options="{ size: 'xs' }">
		<div class="arm-nav__mobile">
			<shared-main-nav :unread-news-count="setup.unreadNewsCount"/>
		</div>
	</o-aside>

	<o-loader
		id="navigation-loader"
		:ref="setup._navigationLoader"/>
</template>

<script setup lang="ts">
// #region Setup
import type { SharedLayoutEmits, SharedLayoutProps } from './SharedLayoutSetup';
import SharedLayoutSetup from './SharedLayoutSetup';
const emits = defineEmits<SharedLayoutEmits>() as SharedLayoutEmits;
const props = withDefaults(defineProps<SharedLayoutProps>(), SharedLayoutSetup.defaultProps);
const setup = new SharedLayoutSetup(props, emits);
// #endregion
import TourRegister from '@/components/tour/TourRegister.vue';
import { inject } from 'vue';
import SharedDiscussions from './SharedDiscussions.vue';
import SharedBotpress from './SharedBotpress.vue';
import SharedMainNav from './SharedMainNav.vue';
const appVersion = inject<string>('appVersion');
</script>

<style lang="less" scoped>
.permission-popover {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	min-width: 11rem;
	padding: 0.5rem;

	&__color {
		width: 1rem;
		height: 1rem;
		border-radius: 0.25rem;
	}

	&__item {
		display: flex;
		gap: 0.5rem;
		align-items: center;
		padding: 0.25rem;
	}
}

</style>
